import React, {   useState, useEffect, useMemo } from "react";
import SearchIcon from '@mui/icons-material/Search';
import DashboardHeader from '../../components/Common/DashboardHeader';

import {
  Typography,
  Box,
  List,
  ListItemText,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Tab,
  Tabs,
  Autocomplete,
  TextField,
   
} from "@mui/material";
import { 
  StyledListItem, 
  StyledContainer,
  StyledPieChartBox,    
  StyledFiltersWrapper,
  chartColors,
  CloseButton,
  StyledDialogTitle,
} from "../../style/Admin/AdminTicketStyles";
import {
  getAllSupportTicketsAdmin,
  resolvePendingTicket,
} from "../../services/supportServices";
import { Pie } from "react-chartjs-2";
import {
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Chart as ChartJS,
  PieController,
  ArcElement,
} from "chart.js";
import { toast, ToastContainer } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, PieController, ArcElement);

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];


const currentMonth = new Date().getMonth() + 1;  
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

function AdminTicketDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [inProcessCount, setInProcessCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [pendingChartCount, setPendingChartCount] = useState(0);
  const [resolvedChartCount, setResolvedChartCount] = useState(0);
 
 

  

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const payload = {
        selectedMonth: filter,
        selectedYear,
        offset: rowsPerPage,
        page: page,
        tab: selectedTab === 0 ? "In-Process" : "Resolved",
      };

      const response = await getAllSupportTicketsAdmin(payload);

      if (response?.success) {
        const tickets = response.tickets || [];
        const resolved = tickets.filter(ticket => ticket.status.toLowerCase() === "resolved");
        const pending = tickets.filter(ticket => ticket.status.toLowerCase() === "in-process");

        setResolvedTickets(resolved);
        const pieData = response.pieData;

        const resolvedCount = pieData.resolved;
        const pendingCount = pieData.inProcess;

        setResolvedChartCount(resolvedCount);
        setPendingChartCount(pendingCount);

        setPendingTickets(pending);

        if (selectedTab === 0) {
          setInProcessCount(response.totalCount || pending.length);
        } else {
          setResolvedCount(response.totalCount || resolved.length);
        }
      }
    } catch {
      toast.error("Failed to fetch tickets.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
    setPage(0);
  };

  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTicket(null);
    setRemarks("");
  };

  const handleResolveTicket = async (ticket) => {
    try {
      const payload = { ticketId: ticket.ticketId, remarks };
      const response = await resolvePendingTicket(payload);
      if (response?.success) {
        toast.success("Ticket resolved successfully!");
        setRemarks("");
        fetchTickets();
        handleCloseModal();
      } else {
        toast.error("Failed to resolve the ticket: " + response?.message);
      }
    } catch {
      toast.error("An error occurred while resolving the ticket.");
    }
  };

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const pieData = useMemo(() => ({
    labels: ["Resolved Tickets", "Pending Tickets", "Total"],
    datasets: [
      {
        label: "Ticket Distribution",
        data: [
          resolvedChartCount,
          pendingChartCount,
          resolvedChartCount + pendingChartCount,
        ],
        backgroundColor: [chartColors.resolved, chartColors.pending, chartColors.total],
        borderWidth: 1,
      },
    ],
  }), [resolvedChartCount, pendingChartCount]);

  const currentTabCount = selectedTab === 0 ? inProcessCount : resolvedCount;

  useEffect(() => {
    fetchTickets();
  }, [selectedTab, page,  ]);
  

  return (
    <StyledContainer>
      <ToastContainer />
      <div className="filter-box">
        <DashboardHeader title="Ticket Dashboard" />
        
        <StyledFiltersWrapper>
          <Autocomplete
          size="small" 
            options={months.map((month, index) => ({ label: month, value: index + 1 }))}
            getOptionLabel={(option) => option.label}
            value={{ label: months[filter - 1], value: filter }}
            onChange={(e, newValue) => {
              if (newValue) {
                setFilter(newValue.value);
              }
            }}
            renderInput={(params) => (
              <TextField className="styled_text_field"
                {...params}
                label="Filter by Month"
                variant="outlined"
                size="small"
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />

          <Autocomplete
            options={years.map((year) => ({ label: year.toString(), value: year }))}
            getOptionLabel={(option) => option.label}
            value={{ label: selectedYear.toString(), value: selectedYear }}
            onChange={(e, newValue) => {
              if (newValue) {
                setSelectedYear(newValue.value);
              }
            }}
            renderInput={(params) => (
              <TextField className="styled_text_field"
                {...params}
                label="Filter by Year"
                variant="outlined"
                size="small"
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
            autoHighlight
            openOnFocus
          />

          <Button
            className="styled_button"
            startIcon={<SearchIcon />}
            onClick={() => {
              setPage(0);
              fetchTickets();
            }}
          >
            Search
          </Button>
        </StyledFiltersWrapper>
      </div>

      <Grid container spacing={5} className="chart-grid-container">
        <Grid item xs={12} md={6}>
          <StyledPieChartBox>
            <Box  className="pie_box">
            <Box>
              
                 
                <Typography  className="pie-title" variant="h5">Ticket Distribution</Typography>

                
                <Typography variant="body2" color="textSecondary">
                  {months[filter - 1]} {selectedYear}
                </Typography>
 
              <Pie data={pieData} />
            </Box></Box>
          </StyledPieChartBox>
        </Grid>
      </Grid>

      <Tabs value={selectedTab} onChange={handleTabChange}>
        <Tab className="styled-tab" label="Pending Tickets" />
        <Tab className="styled-tab" label="Resolved Tickets" />
      </Tabs>

      {loading ? (
        <Box className="loader-box">
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {(selectedTab === 0 ? pendingTickets : resolvedTickets).map((ticket) => (
            <StyledListItem key={ticket.ticketId}>
              <Box className="styled-box">
                <ListItemText
                  primary={`Ticket ID: ${ticket.ticketId}`}
                  secondary={`Status: ${ticket.status}`}
                />
                <Button className="styled_button" variant="text" onClick={() => handleOpenModal(ticket)}>
                  View Details
                </Button>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {ticket.createdAt}
                </Typography>
              </Box>
            </StyledListItem>
          ))}
        </List>
      )}

      <TablePagination
        component="div"
        count={currentTabCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} 
      fullWidth
      maxWidth="sm" // or 'md', depending on your desired size
      className="ticket-details-dialog">
        <StyledDialogTitle className="styled_dialog_title">Ticket Details</StyledDialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box>
              <Typography variant="body1"><strong>Ticket ID:</strong> {selectedTicket.ticketId}</Typography>
              <Typography variant="body1"><strong>Status:</strong> {selectedTicket.status}</Typography>
              <Typography variant="body1"><strong>Date:</strong> {selectedTicket.createdAt}</Typography>
              <Typography variant="body1"><strong>Age:</strong> {selectedTicket.age} days</Typography>
              <Typography variant="body1"><strong>ReopenAttempts:</strong> {selectedTicket.reopenAttempts}</Typography>
              <Typography variant="body1"><strong>Subject:</strong> {selectedTicket.subjectType || "N/A"}</Typography>
              <Typography variant="body1"><strong>Message:</strong> {selectedTicket.message || "N/A"}</Typography>
              <Typography variant="body1"><strong>Remarks:</strong> {selectedTicket.remarks || "N/A"}</Typography>
              {selectedTicket?.status.toLowerCase() === "in-process" && (
                <Box mt={2}>
                  <TextField
                  className= "styled_remarks_field"
                    label="Enter Remarks"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {selectedTicket?.status.toLowerCase() === "in-process" && (
            <CloseButton
              variant="contained"
              color="primary"
              onClick={() => handleResolveTicket(selectedTicket)}
              disabled={!remarks.trim()}
            >
              Resolve Ticket
            </CloseButton>
          )}
          <CloseButton    onClick={handleCloseModal}>Close</CloseButton>
        </DialogActions>
        
      </Dialog>
    </StyledContainer>
  );
}

export default AdminTicketDashboard;
